import { useMemo, useState } from "react";
import { PageHeader } from "@/components/layout/PageHeader";
import { DataTable, type Column } from "@/components/ui/DataTable";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { MetricCard } from "@/components/ui/MetricCard";
import { SegmentedFilter } from "@/components/ui/Toolbar";
import { Icon } from "@/components/ui/Icon";
import { useAction, useItem } from "@/lib/api/queries";
import { useSession } from "@/lib/auth/useSession";
import { PERM } from "@/lib/auth/rbac";
import { dateTime, relative, text } from "@/lib/format";

/**
 * 1C solishtirish hisoboti (dev_spec §7.8).
 *
 * Solishtirish HECH NARSANI tuzatmaydi — farq tekshiruv yozuvi bo'lib
 * qoladi, qarorni vakolatli xodim qabul qiladi. Bu ekran ikki ishni
 * qiladi:
 *   1. `DELIVERY_ACK` — davr ichida WMS 1C'ga YUBORGAN, lekin 1C hali
 *      tasdiqlamagan yetkazmalarni topadi (period yetarli, 1C'dan
 *      qo'shimcha ma'lumot kerak emas).
 *   2. Mavjud solishtirishni IDsi bo'yicha ko'rish (avtomatik yoki
 *      hujjat miqdorlari kesimlari — RECEIPT_SHIPMENT/INVENTORY_SLICE —
 *      1C ma'lumotini talab qiladi va boshqa kanaldan yaratiladi).
 */

interface ReconcileRun {
  id: string;
  operation_id: string;
  kind: string;
  status: string;
  total_items: number;
  matched_items: number;
  mismatched_items: number;
  started_at: string;
  finished_at?: string;
}

interface ReconcileItem {
  key: string;
  verdict: string;
  wms_value?: string;
  onec_value?: string;
  details?: Record<string, unknown>;
}

interface ReconcileResult {
  run: ReconcileRun;
  items: ReconcileItem[];
}

const VERDICTS = ["MATCH", "MISMATCH", "MISSING_IN_WMS", "MISSING_IN_1C"] as const;

/** `Date` → datetime-local maydon qiymati ("YYYY-MM-DDTHH:mm", lokal vaqt). */
function localInput(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function Reconciliation({ hint }: { hint: string }) {
  const session = useSession();
  const canReconcile = session.can(PERM.integrationReconcile);

  const now = useMemo(() => new Date(), []);
  const dayAgo = useMemo(() => new Date(now.getTime() - 24 * 3600 * 1000), [now]);
  const [from, setFrom] = useState(localInput(dayAgo));
  const [to, setTo] = useState(localInput(now));
  const [verdict, setVerdict] = useState("");
  const [loadId, setLoadId] = useState("");
  const [idInput, setIdInput] = useState("");

  const run = useAction<ReconcileResult, { from: string; to: string }>(
    "integration",
    (input) => ({
      path: "/api/v1/integration/reconciliations",
      method: "POST",
      body: {
        operation_id: crypto.randomUUID(),
        kind: "DELIVERY_ACK",
        from: new Date(input.from).toISOString(),
        to: new Date(input.to).toISOString(),
      },
    }),
  );

  const loaded = useItem<ReconcileResult>(
    "integration",
    `/api/v1/integration/reconciliations/${loadId}`,
    { enabled: loadId !== "" },
  );

  // Ko'rsatiladigan natija: IDsi bo'yicha ochilgani ustun, aks holda
  // oxirgi ishga tushirilgan solishtirish.
  const result: ReconcileResult | undefined =
    loadId !== "" ? loaded.data : run.data;
  const loadError = loadId !== "" ? loaded.error : run.error;

  const items = useMemo(
    () => (result?.items ?? []).filter((item) => !verdict || item.verdict === verdict),
    [result, verdict],
  );

  const columns: Column<ReconcileItem>[] = [
    { key: "key", header: "Obyekt / xabar", render: (row) => <span className="mono">{row.key}</span> },
    { key: "verdict", header: "Verdikt", render: (row) => <StatusBadge value={row.verdict} /> },
    { key: "wms", header: "WMS", align: "right", render: (row) => <span className="num">{text(row.wms_value)}</span> },
    { key: "onec", header: "1C", align: "right", render: (row) => <span className="num">{text(row.onec_value)}</span> },
    {
      key: "details",
      header: "Izoh",
      render: (row) => {
        const delta = row.details?.delta ?? row.details?.reason;
        return delta ? <span>{String(delta)}</span> : <span style={{ color: "var(--on-surface-variant)" }}>—</span>;
      },
    },
  ];

  const canRun = canReconcile && from !== "" && to !== "" && new Date(from) < new Date(to);

  return (
    <div className="page-inner">
      <PageHeader
        title="1C solishtirish"
        hint={hint}
        actions={
          canReconcile ? (
            <button
              type="button"
              className="btn btn-filled"
              disabled={!canRun || run.isPending}
              onClick={() => {
                setLoadId("");
                run.mutate({ from, to });
              }}
            >
              <Icon name="refresh" size={16} />
              {run.isPending ? "Solishtirilmoqda…" : "Yetkazmalarni solishtirish"}
            </button>
          ) : undefined
        }
      />

      <section className="section">
        <div className="section-title">Yangi solishtirish — 1C tasdiqlamagan yetkazmalar</div>
        <div className="group">
          <div className="grid-2">
            <label className="field">
              <span className="field-label">Davr boshi</span>
              <input
                type="datetime-local"
                className="input"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </label>
            <label className="field">
              <span className="field-label">Davr oxiri</span>
              <input
                type="datetime-local"
                className="input"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </label>
          </div>
          <p className="field-hint">
            WMS shu davrda 1C'ga YUBORGAN (DELIVERED) natijalardan 1C hali
            tasdiqlamaganlari <strong>MISSING_IN_1C</strong> bo'lib chiqadi.
            Solishtirish hech narsani tuzatmaydi.
          </p>
          {!canReconcile && (
            <p className="field-hint">Solishtirish uchun <code>integration.reconcile</code> vakolati kerak.</p>
          )}
        </div>
      </section>

      <section className="section">
        <div className="section-title">Mavjud solishtirishni ochish</div>
        <div className="group">
          <div className="grid-2">
            <label className="field">
              <span className="field-label">Solishtirish IDsi</span>
              <input
                type="text"
                className="input mono"
                placeholder="UUID"
                value={idInput}
                onChange={(e) => setIdInput(e.target.value)}
              />
            </label>
            <div className="field" style={{ justifyContent: "flex-end" }}>
              <button
                type="button"
                className="btn btn-tonal"
                disabled={idInput.trim() === ""}
                onClick={() => setLoadId(idInput.trim())}
              >
                Ochish
              </button>
            </div>
          </div>
        </div>
      </section>

      {loadError && (
        <section className="section">
          <div className="notice">{text((loadError as Error).message)}</div>
        </section>
      )}

      {result && (
        <>
          <section className="metrics">
            <MetricCard label="Jami" value={String(result.run.total_items)} icon="layers" />
            <MetricCard label="Mos" value={String(result.run.matched_items)} accent="success" icon="check" />
            <MetricCard
              label="Farq"
              value={String(result.run.mismatched_items)}
              accent={result.run.mismatched_items > 0 ? "warning" : "success"}
              icon="alert"
            />
          </section>

          <section className="section">
            <div className="section-title">
              {result.run.kind} · {result.run.id}
              {result.run.finished_at && (
                <span style={{ color: "var(--on-surface-variant)" }} title={dateTime(result.run.finished_at)}>
                  {" "}· {relative(result.run.finished_at)}
                </span>
              )}
            </div>
            <div style={{ marginBottom: "var(--space-3)" }}>
              <SegmentedFilter
                value={verdict}
                onChange={setVerdict}
                options={[
                  { value: "", label: "Hammasi" },
                  ...VERDICTS.map((v) => ({ value: v, label: v })),
                ]}
              />
            </div>
            <DataTable
              columns={columns}
              rows={items}
              rowKey={(row) => row.key}
              isLoading={loadId !== "" && loaded.isLoading}
              emptyTitle="Farq topilmadi"
              emptyHint="Tanlangan verdikt bo'yicha yozuv yo'q."
            />
          </section>
        </>
      )}
    </div>
  );
}
